import { capitalizeFirstLetter } from "../../../util";
import {
  WebTracker,
  WebTrackerResolutionType,
  WebTrackerRiskLevel,
  hasVideoProxy,
  isMaps,
  isUninstalled,
  needsResolution,
} from "../../types";

export type WebTrackerFollowUpTask =
  | "completed"
  | "uninstall_tracker"
  | "uninstall_native_tracker"
  | "set_up_destination"
  | "set_up_video_proxy"
  | "set_up_fp_maps"
  | "upload_document"
  | "publish_changes"
  | "choose_resolution";

export function displayName(task: WebTrackerFollowUpTask): string {
  switch (task) {
    case "completed":
      return "completed";
    case "uninstall_tracker":
      return "uninstall tracker";
    case "uninstall_native_tracker":
      return "uninstall native tracker";
    case "set_up_destination":
      return "set up destination in Freshpaint";
    case "set_up_video_proxy":
      return "set up the Freshpaint video proxy";
    case "set_up_fp_maps":
      return "set up Freshpaint maps";
    case "upload_document":
      return "upload document";
    case "publish_changes":
      return "publish changes";
    case "choose_resolution":
      return "choose a resolution";
    default:
      // This is unlikely to ever occur, but we should handle it just in case.
      throw new Error(`Invalid follow-up task: ${task}`);
  }
}

function replaceWithFreshpaintTasks(
  tracker: WebTracker
): FollowUpTasksProgress {
  const progress: FollowUpTasksProgress = new Map();

  let setupIntegrationTask: WebTrackerFollowUpTask;
  if (hasVideoProxy(tracker)) {
    setupIntegrationTask = "set_up_video_proxy";
  } else if (isMaps(tracker)) {
    setupIntegrationTask = "set_up_fp_maps";
  } else {
    setupIntegrationTask = "set_up_destination";
  }

  const integrationIsConfirmed =
    tracker.resolution?.isFPIntegrationConfirmed ?? false;

  progress.set(setupIntegrationTask, integrationIsConfirmed);
  progress.set("uninstall_native_tracker", isUninstalled(tracker));
  return progress;
}

function markedForRemovalTasks(tracker: WebTracker): FollowUpTasksProgress {
  const progress: FollowUpTasksProgress = new Map();
  progress.set("uninstall_tracker", isUninstalled(tracker));
  return progress;
}

function signedBAATasks(tracker: WebTracker): FollowUpTasksProgress {
  const progress: FollowUpTasksProgress = new Map();
  progress.set("upload_document", !!tracker.baa);
  return progress;
}

function trackerBlockerTasks(tracker: WebTracker): FollowUpTasksProgress {
  const progress: FollowUpTasksProgress = new Map();
  progress.set("publish_changes", tracker.isPublishedInBlocker);
  progress.set("uninstall_tracker", isUninstalled(tracker));
  return progress;
}

function unresolvedTasks(
  effectiveRiskLevel: WebTrackerRiskLevel
): FollowUpTasksProgress {
  const progress: FollowUpTasksProgress = new Map();
  if (needsResolution(effectiveRiskLevel)) {
    progress.set("choose_resolution", false);
  }
  return progress;
}

export function getFollowUpTasks(
  resolutionType: WebTrackerResolutionType,
  tracker: WebTracker
): FollowUpTasksProgress {
  switch (resolutionType) {
    case "marked_as_safe":
      return new Map();
    case "marked_for_removal":
      return markedForRemovalTasks(tracker);
    case "replace_with_freshpaint":
      return replaceWithFreshpaintTasks(tracker);
    case "signed_baa":
      return signedBAATasks(tracker);
    case "tracker_blocked":
      return trackerBlockerTasks(tracker);
    case "tracker_first_party":
      return new Map();
    case "unresolved":
      return unresolvedTasks(tracker.effectiveRiskLevel);
    default:
      // This is unlikely to ever occur, but we should handle it just in case.
      throw new Error(`Invalid resolution type: ${resolutionType}`);
  }
}

export type FollowUpTasksProgress = Map<WebTrackerFollowUpTask, boolean>;

// formatIncompleteFollowUpTasks returns incompleted tasks as
// a comma-separated string
export function formatIncompleteFollowUpTasks(
  tasksProgress: FollowUpTasksProgress
): string {
  if (hasNoFollowUpTasks(tasksProgress)) {
    return "";
  }

  const formattedTasks = incompleteTasks(tasksProgress)
    .map((task) => displayName(task))
    .join(", ");
  return capitalizeFirstLetter(formattedTasks);
}

export function hasNoFollowUpTasks(tasksProgress: FollowUpTasksProgress) {
  return tasksProgress.size <= 0;
}

export function incompleteTasks(tasksProgress: FollowUpTasksProgress) {
  const incomplete: WebTrackerFollowUpTask[] = [];
  for (const [task, completed] of tasksProgress) {
    if (!completed) {
      incomplete.push(task);
    }
  }
  return incomplete;
}

export function hasIncompleteTask(
  tasksProgress: FollowUpTasksProgress,
  task: WebTrackerFollowUpTask
) {
  return tasksProgress.has(task) && tasksProgress.get(task) === false;
}
